import { EKeyCode } from '@/enums/e_key_code';
import { CALLBACK_keyboard_event } from '@/types/types';
import { assert_type_boolean } from '@shared/asserts/utils_asserts';
import { delete_value_from_array } from '@shared/utils/utils_array';

class SERVICE__shortcuts_manager {
  //key - keys codes joined with '+'
  private handlers_map_down: Map<string, CALLBACK_keyboard_event[]>;

  private handlers_map_up: Map<string, CALLBACK_keyboard_event[]>;

  private pressed_keys: Set<EKeyCode> = new Set();

  private is_enabled: boolean;

  /*
   * EVENTS
   */
  private onKeyDown(e: KeyboardEvent, handlers_map: Map<string, CALLBACK_keyboard_event[]>) {
    if (!(e instanceof KeyboardEvent)) return; //browser throw error even for trackpad
    if (this.is_typing_inside_tag_input()) return;
    if (!this.is_enabled) return;

    this.pressed_keys.add(e.code as EKeyCode);

    this.handle_key(e, this.pressed_keys.to_array(), handlers_map);
  }

  private onKeyUp(e: KeyboardEvent, handlers_map: Map<string, CALLBACK_keyboard_event[]>, pressed_keys: Set<EKeyCode>) {
    if (!(e instanceof KeyboardEvent)) return; //browser throw error even for trackpad
    if (this.is_typing_inside_tag_input()) return;
    if (!this.is_enabled) return;

    pressed_keys.delete(e.code as EKeyCode);

    this.handle_key(e, [e.code as EKeyCode], handlers_map);
  }

  private handle_key(e: KeyboardEvent, keys: EKeyCode[], handlers_map: Map<string, CALLBACK_keyboard_event[]>) {
    let keys_string = this.get_key_codes_string(keys);
    if (!handlers_map.has(keys_string)) return;

    let callbacks_list = handlers_map.get_or_assert(keys_string);
    if (callbacks_list.is_empty()) return;

    for (let i = callbacks_list.length - 1; i >= 0; i--) {
      let handler = callbacks_list[i];

      let is_handled = handler(e);

      assert_type_boolean(is_handled);

      if (is_handled) {
        e.preventDefault();
        e.stopPropagation();

        break;
      }
    }
  }

  /*
   * PROPERTIES
   */

  set_is_enabled(value: boolean) {
    this.is_enabled = value;
  }

  /*
   * METHODS
   */

  constructor() {
    this.handlers_map_down = new Map();
    this.handlers_map_up = new Map();

    this.is_enabled = true;
    window.addEventListener('keydown', e => this.onKeyDown(e, this.handlers_map_down));
    window.addEventListener('keyup', e => this.onKeyUp(e, this.handlers_map_up, this.pressed_keys)); //todo: review all keyup on app and use for them this service
  }

  /*
   *
   */

  private handler_base__prepend(key_codes: EKeyCode[], callback: CALLBACK_keyboard_event, handlers_map: Map<string, CALLBACK_keyboard_event[]>) {
    let keys_string = this.get_key_codes_string(key_codes);

    let callbacks_list = handlers_map.get_or_create_array(keys_string);
    callbacks_list.unshift(callback);
  }

  handler_down__prepend(key_codes: EKeyCode[], callback: CALLBACK_keyboard_event) {
    this.handler_base__prepend(key_codes, callback, this.handlers_map_down);
  }

  handler_up__prepend(key_codes: EKeyCode[], callback: CALLBACK_keyboard_event) {
    this.handler_base__prepend(key_codes, callback, this.handlers_map_up);
  }

  /*
   *
   */

  private handler_base__append(key_codes: EKeyCode[], callback: CALLBACK_keyboard_event, handlers_map: Map<string, CALLBACK_keyboard_event[]>) {
    let keys_string = this.get_key_codes_string(key_codes);

    let callbacks_list = handlers_map.get_or_create_array(keys_string);
    callbacks_list.push(callback);
  }

  handler_down__append(key_codes: EKeyCode[], callback: CALLBACK_keyboard_event) {
    this.handler_base__append(key_codes, callback, this.handlers_map_down);
  }

  handler_up__append(key_codes: EKeyCode[], callback: CALLBACK_keyboard_event) {
    this.handler_base__append(key_codes, callback, this.handlers_map_up);
  }
  /*
   *
   */

  handler_base__delete(key_codes: EKeyCode[], callback: CALLBACK_keyboard_event, handlers_map: Map<string, CALLBACK_keyboard_event[]>) {
    let keys_string = this.get_key_codes_string(key_codes);

    let callbacks_list = handlers_map.get_or_create_array(keys_string);
    delete_value_from_array(callbacks_list, callback);
  }

  handler_down__delete(key_codes: EKeyCode[], callback: CALLBACK_keyboard_event) {
    this.handler_base__delete(key_codes, callback, this.handlers_map_down);
  }

  handler_up__delete(key_codes: EKeyCode[], callback: CALLBACK_keyboard_event) {
    this.handler_base__delete(key_codes, callback, this.handlers_map_up);
  }

  /*
   *
   */

  is_typing_inside_tag_input(): boolean {
    const { activeElement } = document;

    if (!activeElement) return false;

    const tag = activeElement.tagName;
    const editable = (activeElement as HTMLElement).isContentEditable;

    return tag === 'INPUT' || tag === 'TEXTAREA' || editable;
  }

  reset_pressed_keys() {
    this.pressed_keys.clear();
  }

  get_key_codes_string(key_codes: EKeyCode[]) {
    let key_codes_string = key_codes.map(key => key.toLowerCase());

    key_codes_string.sort();

    return key_codes_string.join('+');
  }
}

export const service_shortcuts_manager = new SERVICE__shortcuts_manager();
